import React, { FC, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import Button from '../../../../../components/actions/Button'
import { ModalContext } from '../../../../../contexts/ModalContext'
import { ObjectContext } from '../../../../../contexts/objectContext/ObjectContext'
import { mockHouseholdsData } from '../../../constants/scoreboardConstants'
import { ScoreboardHook } from '../../../hooks/useScoreboard'
import SimilarHouseholds from './components/SimilarHouseholds'

interface HouseholdComparisonProps {
	data: ScoreboardHook
}

interface HouseholdComparisonMessageProps {
	text: string
	buttonText?: string
	buttonAction?: () => void
}

const HouseholdComparisonMessage: FC<HouseholdComparisonMessageProps> = ({
	text,
	buttonText,
	buttonAction,
}) => {
	return (
		<div className='absolute inset-0 bg-[#f5f5f5]/25 dark:bg-[#0d2d30]/25 backdrop-blur-[1px] rounded-lg flex items-center justify-center'>
			<div className='flex flex-col gap-3 bg-white dark:bg-[#063a3e] rounded-xl px-6 py-4 shadow-lg border border-[#c4c4c4] dark:border-brand-primary-100-dark max-w-xs text-center'>
				<span className='text-lg text-text-dark dark:text-brand-primary-100-dark block'>
					{text}
				</span>
				{buttonAction && buttonText && (
					<Button onClick={buttonAction} className='m-0'>
						{buttonText}
					</Button>
				)}
			</div>
		</div>
	)
}

const HouseholdComparison: FC<HouseholdComparisonProps> = ({ data }) => {
	const { t } = useTranslation('pages_saving', { keyPrefix: 'similarHouseholds' })
	const { squares, personCount, type } = useContext(ObjectContext)
	const { insertModal } = useContext(ModalContext)

	const isQualifiedForComparison = !!squares && !!personCount && !!type

	const handleClick = () => {
		if (!isQualifiedForComparison) {
			insertModal('settings-objectBasics')
		}
	}

	return (
		<div className='flex flex-col rounded-xl gap-2'>
			<span className='text-center font-semibold text-lg tracking-wide text-text-dark dark:text-white'>
				{t('previousPeriod')}
			</span>
			<div className='grid grid-cols-2 gap-4 relative'>
				{isQualifiedForComparison ? (
					!data.bestSimilarHouseholds && !data.isLoading ? (
						<>
							<HouseholdComparisonMessage text='Näet vertailutietoja, kun olemme saaneet riittävästi tietoja' />
							<SimilarHouseholds
								bestHouseholdsData={[...mockHouseholdsData]}
								type='points'
								isLoading={true}
							/>
							<SimilarHouseholds
								bestHouseholdsData={[...mockHouseholdsData]}
								type='savings'
								isLoading={true}
							/>
						</>
					) : (
						<>
							<SimilarHouseholds
								bestHouseholdsData={
									data.bestSimilarHouseholds && Array.isArray(data.bestSimilarHouseholds.points)
										? data.bestSimilarHouseholds.points.slice()
										: []
								}
								type='points'
								isLoading={data.isLoading}
							/>
							<SimilarHouseholds
								bestHouseholdsData={
									data.bestSimilarHouseholds && Array.isArray(data.bestSimilarHouseholds.savings)
										? data.bestSimilarHouseholds.savings.slice()
										: []
								}
								type='savings'
								isLoading={data.isLoading}
							/>
						</>
					)
				) : (
					<>
						<HouseholdComparisonMessage
							text='Täytä kohteen tiedot nähdäksesi vertailutiedot'
							buttonText='Täytä tiedot'
							buttonAction={handleClick}
						/>
						<SimilarHouseholds
							bestHouseholdsData={[...mockHouseholdsData]}
							type='points'
							isLoading={true}
						/>
						<SimilarHouseholds
							bestHouseholdsData={[...mockHouseholdsData]}
							type='savings'
							isLoading={true}
						/>
					</>
				)}
			</div>
		</div>
	)
}

export default HouseholdComparison
